import React from 'react';
import PropTypes from 'prop-types';
import { pureRender } from 'epic-alt-utils/index';
import debounce from 'lodash.debounce';
import root from 'window-or-global';
import { Locale, Search, CtaButton, SignIn, SignedIn, SiteNav, SearchExtension, MobileMenuButtons } from '../';
import NavActions from '../../actions';
import { getWindowWidth, getWindowHeight } from 'epic-common/src/browser';

@pureRender
export default class RightNav extends React.Component {
    constructor(props) {
        super(props);

        //the min width the active search input box can be set to
        this.minWidth = 250;

        this.rightNavWidth = 0;

        this.state = {
            searchMaxWidth: 0,
            maxWidth: 0,
            mobileCollapsed: false
        };

        this.debounceResize = debounce(() => {
            if (this.dead) return;

            if (this.state.mobileCollapsed && getWindowHeight() > getWindowWidth()) {
                this.setState({
                    mobileCollapsed: false
                });
            }
        }, 300);

        this.resizeListenerSet = false;
    }

    static propTypes = {
        searchActive: PropTypes.bool,
        isLoggedIn: PropTypes.bool,
        hideLocale: PropTypes.bool,
        hideSearch: PropTypes.bool,
        hideSignIn: PropTypes.bool,
        hideCallToAction: PropTypes.bool,
        callToAction: PropTypes.object,
        links: PropTypes.array,
        localeOptions: PropTypes.array,
        mobileActiveLinkIndex: PropTypes.number,
        mobileLocaleActive: PropTypes.bool,
        mobileUserActive: PropTypes.bool,
        displayName: PropTypes.string,
        locale: PropTypes.string,
        isActive: PropTypes.bool,
        showLinks: PropTypes.bool,
        accountLinks: PropTypes.object,
        onSearch: PropTypes.func,
        maxWidth: PropTypes.number
    };

    static contextTypes = {
        messages: PropTypes.object,
        isMobile: PropTypes.bool,
        isTablet: PropTypes.bool
    };

    showMenu = () => {
        if (!this.props.isActive) {
            NavActions.mobileNavActive(true);
            NavActions.mobileNavActiveLinks(true);
        } else {
            NavActions.mobileNavActive(false);
            NavActions.mobileNavActiveLinks(false);
        }

        //Reset all other clickable items
        NavActions.egNavActive(false);
        NavActions.tabletNavActive(false);
        NavActions.mobileLocaleActive(false);
        NavActions.mobileActiveLinkIndex(null);
        NavActions.searchActive(false);
        NavActions.mobileUserActive(false);
    };

    toggleNavCTA = () => {
        if (!this.props.isActive) {
            NavActions.CTAclick(true);
        } else {
            NavActions.CTAclick(false);
        }
    };

    toggleIconBar = (e) => {
        if (e) e.preventDefault();

        this.setState({
            mobileCollapsed: !this.state.mobileCollapsed
        });
    };

    componentDidMount() {
        setTimeout(() => {
            //get the width of the cta component for calculating width in tablet mode
            const cta = document.getElementById('cta');
            this.rightNavWidth = 0;
            if (cta) {
                this.rightNavWidth = cta.offsetWidth || 220; //220 is max-width for cta anchor
            } else {
                this.rightNavWidth = 220;
            }

            //search, locale, and user are hardcoding to prevent over querying the dom for the static numbers
            this.rightNavWidth += 60; //search width
            this.rightNavWidth += 60; //locale width
            this.rightNavWidth += 60; //user width

            this.determineMaxWidth(this.props.maxWidth);
        }, 1500);

        this.addResizeListener();
    }

    componentWillReceiveProps(nextProps) {
        if (this.context.isTablet && (this.state.maxWidth !== nextProps.maxWidth || this.state.searchMaxWidth < 0)) {
            this.determineMaxWidth(nextProps.maxWidth);
        }

        this.addResizeListener();
    }

    componentWillUnmount() {
        this.dead = true;
        if (this.resizeListenerSet) {
            root.removeEventListener('resize', this.debounceResize);
        }
    }

    addResizeListener() {
        if (this.context.isMobile && !this.resizeListenerSet) {
            root.addEventListener('resize', this.debounceResize);
            this.resizeListenerSet = true;
        }
    }

    determineMaxWidth(maxWidth) {
        if (this.dead) return;

        let width = maxWidth || this.minWidth;

        if (width > this.minWidth) {
            width -= this.rightNavWidth;
        } else {
            width = this.minWidth;
        }

        this.setState({
            searchMaxWidth: width,
            maxWidth
        });
    }

    render() {
        const isMobile = this.context.isMobile || false;
        const accountLinks = this.props.accountLinks || {};

        let hideCallToAction = this.props.hideCallToAction;
        if (!hideCallToAction) {
            if (!this.props.callToAction || !this.props.callToAction.title) {
                console.warn('[EGH] callToAction object invalid or callToAction title was undefined. Hiding callToAction...');
                hideCallToAction = true;
            }
        }

        let hideLocale = this.props.hideLocale;
        if (!hideLocale) {
            if (!this.props.localeOptions || !this.props.localeOptions.length || this.props.localeOptions.length === 1) {
                console.warn('[EGH] localeOptions array was invalid or empty. Hiding locale...');
                hideLocale = true;
            }
        }

        let hideSignIn = this.props.hideSignIn;
        if (!hideSignIn && !this.props.isLoggedIn) {
            if (!accountLinks.signInLinks || !accountLinks.signInLinks.length) {
                console.warn('[EGH] signInLinks array was invalid or empty. Hiding the signIn...');
                hideSignIn = true;
            }
        }

        let hideSearch = this.props.hideSearch;
        if (!hideSearch && !this.props.onSearch) {
            console.warn('[EGH] onSearch is invalid. Hiding search...');
            hideSearch = true;
        }

        const userCompontent = (
            this.props.isLoggedIn ? (
                <SignedIn
                    displayName={this.props.displayName}
                    isActive={this.props.mobileUserActive}
                    signOutLink={accountLinks.signOutLink}
                    dropdownLinks={accountLinks.dropdownLinks}
                    hideCallToAction={hideCallToAction} />
            ) : (
                <SignIn
                    isActive={this.props.mobileUserActive}
                    signInLinks={accountLinks.signInLinks}
                    hideCallToAction={hideCallToAction} />
            )
        );

        const ctaComponent = <CtaButton cta={this.props.callToAction} toggleNav={this.toggleNavCTA} />;

        const localeComponent = (
            <Locale
                localeOptions={this.props.localeOptions}
                isActive={this.props.mobileLocaleActive}
                locale={this.props.locale}
                hideCallToAction={hideCallToAction}
                hideSignIn={hideSignIn} />
        );

        const active = this.props.isActive;

        let hideMobileHamburger = false;
        if (this.props.links && this.props.links.length === 0 && hideCallToAction && hideLocale && hideSignIn && hideSearch) {
            hideMobileHamburger = true;
        }

        return (
            <div id="rightNav" className={`right-col ${active ? 'active' : ''} ${isMobile ? 'rightnav-bg-color' : 'bg-color'}`}>
                {isMobile && !hideMobileHamburger ?
                    <div
                        role="button"
                        tabIndex="0"
                        className={`menu-icon ${active ? 'rightnav-bg-color' : 'accent-bg-color'}`}
                        onClick={this.showMenu}>
                        <span
                            className={`lines ${active ? 'text-color text-color-before-after' : 'accent-text-color-bg accent-text-before-after'}`} />
                    </div>
                    : ''}

                {isMobile ?
                    <div>
                        <div
                            role="button"
                            tabIndex="0"
                            className={`mobile-overlay ${active ? 'active' : ''}`}
                            onClick={this.showMenu} />
                        <div className={`rightNav bg-color ${this.props.searchActive ? 'search-focus' : ''}`}>
                            <div
                                id="iconBar"
                                className={`icon-bar-wrapper ${this.props.hideCallToAction ? 'cta-hidden' : ''}`}>

                                <a href="" className={`icon-bar-toggle ${this.state.mobileCollapsed ? 't-up' : 't-down'}`} onClick={this.toggleIconBar}>
                                    <span className="toggle text-color-border-after" />
                                </a>

                                <div className={`icon-bar accent-bg-color-before ${this.state.mobileCollapsed ? 'shrink' : ''}`}>

                                    {!hideSearch ?
                                        <SearchExtension
                                            active={this.props.searchActive}
                                            onSearch={this.props.onSearch}
                                            searchMaxWidth={this.state.searchMaxWidth} /> : <div />}

                                    <MobileMenuButtons
                                        localeOptions={this.props.localeOptions}
                                        isLocaleActive={this.props.mobileLocaleActive}
                                        locale={this.props.locale}
                                        isLoggedIn={this.props.isLoggedIn}
                                        mobileUserActive={this.props.mobileUserActive}
                                        hideLocale={hideLocale}
                                        hideSignIn={hideSignIn}
                                        signInLinks={accountLinks.signInLinks}
                                        displayName={this.props.displayName} />

                                    {!hideLocale ? localeComponent : <div />}

                                    {!hideSignIn ? userCompontent : <div />}

                                </div>

                            </div>

                            <div className={`${this.state.mobileCollapsed ? 'mobile-collapsed hidden' : ''}`}>
                                {!hideCallToAction ? ctaComponent : <div />}
                            </div>

                            <div className="mobile-links accent-bg-color-before">
                                <SiteNav
                                    links={this.props.links}
                                    mobileActiveLinkIndex={this.props.mobileActiveLinkIndex}
                                    showLinks={this.props.showLinks} />
                            </div>
                        </div>
                    </div>
                    :
                    <div className="rightNav bg-color">
                        {!hideSearch ?
                            <Search
                                isActive={this.props.searchActive}
                                onSearch={this.props.onSearch}
                                searchMaxWidth={this.state.searchMaxWidth} /> : <div />}

                        {!hideLocale ? localeComponent : <div />}

                        {!hideSignIn ? userCompontent : <div />}

                        {!hideCallToAction ? ctaComponent : <div />}
                    </div>
                }
            </div>
        );
    }
}



// WEBPACK FOOTER //
// ./app/scripts/components/rightNav/RightNav.js